import { useState } from 'react';
import { areaDefinition } from '../../domain/areaCatalog';
import { floorCountOf } from '../../domain/housePlan';
import type { V2Area } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { floorLabelFor, areaDisplayName } from '../../i18n/labels';
import { useStore } from '../../state/createStore';
import { areaDataSummary, deleteAreaAction, type AreaDataSummary } from '../../state/deleteArea';
import { evidenceStore } from '../../state/evidenceStore';
import { findingStore } from '../../state/findingStore';
import { areasOf, inspectionStore, moveAreaToFloorAction, renameAreaAction, reorderAreaAction, setAreaEnabledAction } from '../../state/inspectionStore';
import { ActionSheet, Button, Chip, ConfirmSheet, Field, InlineMessage, SheetShell, TextInput, showToast } from '../../ui/components';

// The options of an area row (Authority §6.4): Rename · Move to Floor · Move Up / Down ·
// Disable / Enable · Delete. Every step is a sheet with Cancel (OC §9.4). Delete keeps the KEEP
// guard: an area with any inspection record is refused with the reason, and only an empty area
// is deleted after a second confirmation (src/state/deleteArea.ts). Property Layout's row pencil
// and trash (Figma 08) open straight into Rename / Delete through `intent`; a trash tap on an area
// that has records explains why and offers Disable instead.
type View = 'menu' | 'rename' | 'move' | 'delete' | 'deleteBlocked';

export function describeAreaDataFor(tp: ReturnType<typeof useT>['tp'], t: ReturnType<typeof useT>['t'], summary: AreaDataSummary): string {
  const parts: string[] = [];
  if (summary.settledItems > 0) parts.push(tp('layout.data.settled', summary.settledItems));
  if (summary.annotatedItems > 0) parts.push(tp('layout.data.annotated', summary.annotatedItems));
  if (summary.photos > 0) parts.push(tp('common.photosCount', summary.photos));
  if (summary.findings > 0) parts.push(tp('common.findingsCount', summary.findings));
  if (summary.hasAreaNote) parts.push(t('layout.data.areaNote'));
  return parts.length > 0 ? parts.join(' · ') : t('layout.data.none');
}

export function AreaOptionsSheet(props: { area: V2Area; onClose: () => void; intent?: 'menu' | 'rename' | 'delete' }) {
  const { t, tp, locale } = useT();
  const area = useStore(inspectionStore, (s) => s.areas.find((a) => a.id === props.area.id) ?? props.area);
  const siblings = useStore(inspectionStore, (s) => s.areas);
  // Subscribed so the delete guard follows photos and findings recorded meanwhile.
  useStore(evidenceStore, (s) => s.evidence.length);
  useStore(findingStore, (s) => s.findings.length);
  const [view, setView] = useState<View>(() => {
    if (props.intent === 'rename') return 'rename';
    if (props.intent === 'delete') return areaDataSummary(props.area.id).hasData ? 'deleteBlocked' : 'delete';
    return 'menu';
  });
  const [name, setName] = useState(areaDisplayName(locale, area));
  const [floor, setFloor] = useState<number | null>(area.floor);
  const [blocked, setBlocked] = useState<string | null>(null);

  const summary = areaDataSummary(area.id);
  const allAreas = areasOf({ ...inspectionStore.get(), areas: siblings }, area.inspectionId);
  const floorCount = floorCountOf(allAreas);
  const floorBound = areaDefinition(area.kind).group === 'rooms' || area.kind === 'other';
  const floorOptions: Array<{ value: number | null; label: string; testId: string }> = [
    ...Array.from({ length: Math.max(floorCount, 1) }, (_, i) => ({ value: i + 1, label: floorLabelFor(t, i + 1), testId: `move-floor-${i + 1}` })),
    { value: Math.max(floorCount, 1) + 1, label: t('layout.newFloor', { floor: floorLabelFor(t, Math.max(floorCount, 1) + 1) }), testId: 'move-floor-new' },
    { value: null, label: t('layout.noFloor'), testId: 'move-floor-none' }
  ];

  async function commitRename() {
    const trimmed = name.trim();
    if (trimmed.length > 0 && trimmed !== areaDisplayName(locale, area)) await renameAreaAction(area.id, trimmed);
    props.onClose();
  }

  async function commitMove() {
    if (floor !== area.floor) await moveAreaToFloorAction(area.id, floor);
    props.onClose();
  }

  async function reorder(direction: 'up' | 'down') {
    await reorderAreaAction(area.id, direction);
    props.onClose();
  }

  async function toggleEnabled() {
    await setAreaEnabledAction(area.id, !area.enabled);
    props.onClose();
  }

  function requestDelete() {
    if (summary.hasData) {
      setBlocked(t('layout.deleteBlocked', { name: areaDisplayName(locale, area), data: describeAreaDataFor(tp, t, summary) }));
      return;
    }
    setView('delete');
  }

  async function confirmDelete() {
    const result = await deleteAreaAction(area.id);
    if (result.ok) showToast(t('common.delete') + ' · ' + areaDisplayName(locale, area), 'info');
    props.onClose();
  }

  if (view === 'rename') {
    return (
      <SheetShell
        title={t('layout.renameTitle')}
        subtitle={areaDisplayName(locale, area)}
        onClose={props.onClose}
        testId="area-rename-sheet"
        footer={
          <>
            <Button onClick={() => void commitRename()} disabled={name.trim().length === 0} testId="rename-save">
              {t('common.save')}
            </Button>
            <Button variant="text" onClick={props.onClose}>
              {t('shell.cancel')}
            </Button>
          </>
        }
      >
        <Field label={t('layout.areaName')} htmlFor="area-rename">
          <TextInput id="area-rename" value={name} onChange={setName} testId="rename-input" />
        </Field>
      </SheetShell>
    );
  }

  if (view === 'move') {
    return (
      <SheetShell
        title={t('layout.moveTitle', { name: areaDisplayName(locale, area) })}
        onClose={props.onClose}
        testId="area-move-sheet"
        footer={
          <>
            <Button onClick={() => void commitMove()} testId="move-save">
              {t('common.save')}
            </Button>
            <Button variant="text" onClick={props.onClose}>
              {t('shell.cancel')}
            </Button>
          </>
        }
      >
        <div className="chips">
          {floorOptions.map((opt) => (
            <Chip key={opt.testId} label={opt.label} selected={opt.value === floor} onClick={() => setFloor(opt.value)} testId={opt.testId} />
          ))}
        </div>
      </SheetShell>
    );
  }

  if (view === 'deleteBlocked') {
    return (
      <SheetShell
        title={t('layout.deleteBlockedTitle', { name: areaDisplayName(locale, area) })}
        onClose={props.onClose}
        testId="area-delete-blocked"
        footer={
          <>
            {area.enabled ? (
              <Button variant="secondary" icon="block" onClick={() => void toggleEnabled()} testId="area-disable-instead">
                {t('layout.disableInstead')}
              </Button>
            ) : null}
            <Button variant="text" onClick={props.onClose} testId="area-delete-blocked-cancel">
              {t('shell.cancel')}
            </Button>
          </>
        }
      >
        <InlineMessage kind="err" text={t('layout.deleteBlocked', { name: areaDisplayName(locale, area), data: describeAreaDataFor(tp, t, summary) })} />
      </SheetShell>
    );
  }

  if (view === 'delete') {
    return (
      <ConfirmSheet
        title={t('layout.deleteTitle', { name: areaDisplayName(locale, area) })}
        body={t('layout.deleteBody')}
        confirmLabel={t('common.delete')}
        danger
        onConfirm={() => void confirmDelete()}
        onCancel={props.onClose}
      />
    );
  }

  return (
    <ActionSheet
      title={areaDisplayName(locale, area)}
      subtitle={t('layout.areaOptions')}
      onClose={props.onClose}
      testId="area-options-sheet"
      items={[
        { key: 'rename', label: t('layout.rename'), icon: 'edit', onSelect: () => setView('rename'), testId: 'area-option-rename' },
        ...(floorBound ? [{ key: 'move', label: t('layout.moveToFloor'), icon: 'stairs' as const, onSelect: () => setView('move'), testId: 'area-option-move' }] : []),
        { key: 'up', label: t('layout.moveUp'), icon: 'arrow_upward', onSelect: () => void reorder('up'), testId: 'area-option-up' },
        { key: 'down', label: t('layout.moveDown'), icon: 'arrow_downward', onSelect: () => void reorder('down'), testId: 'area-option-down' },
        {
          key: 'enabled',
          label: area.enabled ? t('layout.disable') : t('layout.enable'),
          icon: area.enabled ? 'block' : 'check_circle',
          onSelect: () => void toggleEnabled(),
          testId: 'area-option-toggle'
        },
        { key: 'delete', label: t('common.delete'), icon: 'delete', tone: 'danger', onSelect: requestDelete, testId: 'area-option-delete' }
      ]}
    >
      {blocked ? <InlineMessage kind="err" text={blocked} /> : null}
    </ActionSheet>
  );
}
